/**
 * GenerateWizard
 *
 * Multi-step wizard for generating a week's meal plan.
 * Step 1: Select meal slots (B/L/D per day)
 * Step 2: Dietary preferences (vegetarian/fish)
 * Step 3: Portion selection
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  SafeAreaView,
  TouchableOpacity,
  Animated,
} from 'react-native';
import Feather from '@expo/vector-icons/Feather';
import { Paprika, PaprikaTypography, PaprikaSpacing, PaprikaRadius } from '../../styles/theme';
import { MealType } from '../../redux/mealPlanSlice';

import MealSlotStep from './wizard/MealSlotStep';
import DietaryStep, { DietaryPreferences } from './wizard/DietaryStep';
import PortionStep from './wizard/PortionStep';
import WizardNavigation from './wizard/WizardNavigation';

// Types
export interface MealSlot {
  date: string;
  mealType: MealType;
}

export interface GenerateConfig {
  selectedSlots: MealSlot[];
  dietaryPreferences: DietaryPreferences;
  servings: number;
}

interface GenerateWizardProps {
  visible: boolean;
  onClose: () => void;
  onGenerate: (config: GenerateConfig) => void;
  isGenerating: boolean;
  weekStartDate: string;
}

const TOTAL_STEPS = 3;

export const GenerateWizard: React.FC<GenerateWizardProps> = ({
  visible,
  onClose,
  onGenerate,
  isGenerating,
  weekStartDate,
}) => {
  // Step state
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Selected meal slots (format: "YYYY-MM-DD-breakfast")
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());

  // Step 2: Dietary preferences
  const [dietaryPreferences, setDietaryPreferences] = useState<DietaryPreferences>({
    includeVegetarian: false,
    includeFish: false,
    noPreference: true,
  });

  // Step 3: Servings
  const [servings, setServings] = useState(4);

  // Reset wizard state when closed
  const handleClose = useCallback(() => {
    setCurrentStep(1);
    setSelectedSlots(new Set());
    setDietaryPreferences({
      includeVegetarian: false,
      includeFish: false,
      noPreference: true,
    });
    setServings(4);
    onClose();
  }, [onClose]);

  // Toggle meal slot selection
  const handleToggleSlot = useCallback((slotKey: string) => {
    setSelectedSlots(prev => {
      const newSet = new Set(prev);
      if (newSet.has(slotKey)) {
        newSet.delete(slotKey);
      } else {
        newSet.add(slotKey);
      }
      return newSet;
    });
  }, []);

  // Toggle dietary preference
  const handleTogglePreference = useCallback((key: keyof DietaryPreferences) => {
    setDietaryPreferences(prev => {
      // If toggling "no preference", turn off other options
      if (key === 'noPreference') {
        return {
          includeVegetarian: false,
          includeFish: false,
          noPreference: !prev.noPreference,
        };
      }

      // If toggling a specific preference, turn off "no preference"
      const newPrefs = {
        ...prev,
        [key]: !prev[key],
        noPreference: false,
      };

      // If no specific preferences are selected, default back to "no preference"
      if (!newPrefs.includeVegetarian && !newPrefs.includeFish) {
        newPrefs.noPreference = true;
      }

      return newPrefs;
    });
  }, []);

  // Navigation handlers
  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Final step - generate!
      const slots: MealSlot[] = Array.from(selectedSlots).map(key => {
        const parts = key.split('-');
        // Format: "YYYY-MM-DD-mealType"
        const date = parts.slice(0, 3).join('-');
        const mealType = parts[3] as MealType;
        return { date, mealType };
      });

      onGenerate({
        selectedSlots: slots,
        dietaryPreferences,
        servings,
      });
    }
  }, [currentStep, selectedSlots, dietaryPreferences, servings, onGenerate]);

  // Validation for each step
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return selectedSlots.size > 0;
      case 2:
        return true; // Always valid (has default)
      case 3:
        return servings > 0;
      default:
        return false;
    }
  }, [currentStep, selectedSlots.size, servings]);

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <MealSlotStep
            weekStartDate={weekStartDate}
            selectedSlots={selectedSlots}
            onToggleSlot={handleToggleSlot}
          />
        );
      case 2:
        return (
          <DietaryStep
            preferences={dietaryPreferences}
            onTogglePreference={handleTogglePreference}
          />
        );
      case 3:
        return (
          <PortionStep
            servings={servings}
            onServingsChange={setServings}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="fullScreen"
      onRequestClose={handleClose}
    >
      <SafeAreaView style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={handleClose}
            disabled={isGenerating}
          >
            <Feather name="x" size={24} color={Paprika.textPrimary} />
          </TouchableOpacity>

          {/* Step Indicator */}
          <View style={styles.stepIndicator}>
            {Array.from({ length: TOTAL_STEPS }).map((_, index) => (
              <View
                key={index}
                style={[
                  styles.stepDot,
                  index + 1 === currentStep && styles.stepDotActive,
                  index + 1 < currentStep && styles.stepDotCompleted,
                ]}
              />
            ))}
          </View>

          {/* Step Label */}
          <Text style={styles.stepLabel}>Step {currentStep} of {TOTAL_STEPS}</Text>
        </View>

        {/* Step Content */}
        <View style={styles.content}>{renderStepContent()}</View>

        {/* Navigation */}
        <WizardNavigation
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
          onBack={handleBack}
          onNext={handleNext}
          canProceed={canProceed}
          isLastStep={currentStep === TOTAL_STEPS}
          isGenerating={isGenerating}
        />
      </SafeAreaView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Paprika.background,
  },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.borderLight,
  },
  closeButton: {
    padding: PaprikaSpacing.xs,
  },
  stepIndicator: {
    flexDirection: 'row',
    gap: PaprikaSpacing.xs,
  },
  stepDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Paprika.borderLight,
  },
  stepDotActive: {
    backgroundColor: Paprika.textPrimary,
    width: 24,
  },
  stepDotCompleted: {
    backgroundColor: Paprika.textSecondary,
  },
  stepLabel: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  // Content
  content: {
    flex: 1,
  },
});

export default GenerateWizard;
